import React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import PropTypes from "prop-types";

import DgDivider from "./DgDivider";
import DialogPopup from "../../../DialogPopup";
import { getParsedValue } from "../../services/form.service";
import useDatagridState from "../../hooks/useDatagridState";
import {
  getRecordsToRemove,
  getRecordsToUpdate,
} from "../../services/form.relationalDg.service";

function DgDialog({ dialogProps, setDialogProps, removeRecord, saveRecord }) {
  const { type, custom, associatedDgList } = dialogProps;
  const { getDgState } = useDatagridState();
  let qnLabel = "";
  if (type === "invalidControllingField") {
    const customProps = getParsedValue(custom, "DgDialog") || {};
    qnLabel = customProps.qnLabel || "";
  }

  const onClickHandler = () => {
    if (type === "removeRecord") removeRecord(true);
    else if (type === "invalidControllingField") saveRecord(null, true);
    setDialogProps(null);
  };

  const buttonArray = [
    {
      id: 1,
      name: "Cancel",
      variant: "contained",
      onClickHandler: () => setDialogProps(null),
      show: true,
    },
    {
      id: 2,
      name: "Continue",
      variant: "contained",
      onClickHandler,
      show: true,
    },
  ];

  const renderDatagridSection = (records, title, chipColor, dgConfigs) => {
    if (!records.length) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
          {title}
        </Typography>
        {records.map(({ dgKey, records: dgRecords }) => {
          const dgTitle = dgConfigs?.[dgKey]?.title || dgKey;
          return (
            <Box key={dgKey} sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>{dgTitle}</strong>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {dgRecords.map(({ index }) => (
                  <Chip
                    key={`${dgKey}-${index}`}
                    label={`Record #${index + 1}`}
                    size="small"
                    variant="outlined"
                    color={chipColor}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderAssociatedDatagrids = () => {
    if (!Array.isArray(associatedDgList) || !associatedDgList?.length)
      return null;
    const { dgConfigs } = getDgState();
    const recordsToRemove = getRecordsToRemove(associatedDgList);
    const recordsToUpdate = getRecordsToUpdate(associatedDgList);
    const showDivider = recordsToRemove.length && recordsToUpdate.length;

    return (
      <Box sx={{ mt: 2 }}>
        {renderDatagridSection(
          recordsToRemove,
          "The following associated records will be permanently removed:",
          "error",
          dgConfigs
        )}
        {showDivider ? <DgDivider /> : null}
        {renderDatagridSection(
          recordsToUpdate,
          "The following records will have this association removed (records will remain):",
          "warning",
          dgConfigs
        )}
      </Box>
    );
  };

  const removeRecordWarningProps = {
    title: "Warning - Associated records found with this entry.",
    content: (
      <Box>
        <Typography sx={{ mb: 1 }}>
          This entry has associated records that will be affected by this
          deletion.
        </Typography>
        {renderAssociatedDatagrids()}
        <Typography
          variant="body2"
          sx={{ mt: 2, fontStyle: "italic", color: "text.secondary" }}
        >
          Please review the impact and confirm to proceed.
        </Typography>
      </Box>
    ),
  };

  const invalidFieldWarningProps = {
    title: "Warning",
    content: (
      <>
        <Typography>
          You have updated the following listed item and this will cause the
          associated records to be removed permanently and cannot be reverted.
          Please confirm.
        </Typography>
        <ul>
          <li>
            <Typography>{qnLabel}</Typography>{" "}
          </li>
        </ul>
      </>
    ),
  };

  const dialogPropsMap = {
    removeRecord: removeRecordWarningProps,
    invalidControllingField: invalidFieldWarningProps,
  };

  const title = dialogPropsMap[type]?.title || "";
  const content = dialogPropsMap[type]?.content || "";

  return (
    <DialogPopup
      title={title}
      content={content}
      buttonArray={buttonArray}
      openPopup
      onCancel={undefined}
      showActionBtn
      maxWidth="md"
      hideIcon="none"
    />
  );
}

export default DgDialog;

DgDialog.propTypes = {
  dialogProps: PropTypes.shape({
    type: PropTypes.string.isRequired,
    custom: PropTypes.string,
    associatedDgList: PropTypes.arrayOf(
      PropTypes.shape({
        dgKey: PropTypes.string.isRequired,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            index: PropTypes.number.isRequired,
            hasMultiAssociation: PropTypes.bool.isRequired,
          })
        ).isRequired,
      })
    ),
  }).isRequired,
  setDialogProps: PropTypes.func.isRequired,
  removeRecord: PropTypes.func.isRequired,
  saveRecord: PropTypes.func.isRequired,
};
